// Interfaces

export interface CustomCompOptionProps {
  prefix?: string
  suffix?: string
  label?: string | string[]
  sublabel?: string
  options: Array<{ name: string | null; value: string | number | null }>
  fixedLabel?: string
}

export interface CustomCompPillProps {
  name: string
  value: string
  selected?: boolean
  size?: string
}

// CustomizeComps Constants

export type CustomCompDescriptionType =
  | "Standard"
  | "Months ago"
  | "Years ago"
  | "Months and Years ago"
  | "Percentage"

// Distances within MSA
export const DISTANCES_MSA: CustomCompOptionProps = {
  prefix: "Up to",
  suffix: "away",
  label: "mi",
  options: [
    { name: "0.25", value: 0.25 },
    { name: "0.5", value: 0.5 },
    { name: "1", value: 1 },
    { name: "1.5", value: 1.5 },
    { name: "2", value: 2 },
    { name: "3", value: 3 },
    { name: "4", value: 4 },
    { name: "5", value: 5 },
  ],
}

// Distances outside MSA
export const DISTANCES_NON_MSA: CustomCompOptionProps = {
  prefix: "Up to",
  suffix: "away",
  label: "mi",
  options: [
    { name: "1", value: 1 },
    { name: "5", value: 5 },
    { name: "10", value: 10 },
    { name: "15", value: 15 },
    { name: "20", value: 20 },
    { name: "25", value: 25 },
    { name: "30", value: 30 },
  ],
}

export const SOLD_DATES: CustomCompOptionProps = {
  prefix: "Up to",
  suffix: "ago",
  label: ["month", "year"],
  options: [
    { name: "1m", value: 30 }, // 1 month -> 30 days
    { name: "2m", value: 60 }, // 2 months -> 60 days
    { name: "3m", value: 90 }, // 3 months -> 90 days
    { name: "6m", value: 180 }, // 6 months -> 180 days
    { name: "1y", value: 365 }, // 1 year -> 365 days
    { name: "1.5y", value: 547 }, // 1.5 years -> 547 days (approx.)
    { name: "2y", value: 730 }, // 2 years -> 730 days
  ],
}

export const LISTING_STATUSES: CustomCompPillProps[] = [
  { name: "SOLD", value: "sold" },
  { name: "ACTIVE", value: "active" },
  { name: "CONTINGENT", value: "contingent" },
  { name: "PENDING", value: "pending" },
]

export function generateSqFtOptions(
  baseSqFt: number,
  minPercentage: number,
  maxPercentage: number
): {
  minOptions: CustomCompOptionProps
  maxOptions: CustomCompOptionProps
} {
  // Calculate the min and max square footage, rounded down and up to the nearest 100 respectively
  const minSqFt = Math.floor((baseSqFt * (1 - minPercentage / 100)) / 100) * 100
  const maxSqFt = Math.ceil((baseSqFt * (1 + maxPercentage / 100)) / 100) * 100

  const minOptions: CustomCompOptionProps = { label: ["sq ft"], options: [] }
  const maxOptions: CustomCompOptionProps = { label: ["sq ft"], options: [] }

  // Generate min options
  for (let value = minSqFt; value < baseSqFt; value += 100) {
    minOptions.options.push({ name: null, value })
  }

  // Ensure the base square footage is included in minOptions
  if (!minOptions.options.find((option) => option.value === baseSqFt)) {
    minOptions.options.push({ name: null, value: baseSqFt })
  }

  // Generate max options with each step rounding up to the nearest 100
  for (let value = baseSqFt; value <= maxSqFt; value += 100) {
    const roundedValue = Math.ceil(value / 100) * 100
    if (!maxOptions.options.find((option) => option.value === roundedValue)) {
      maxOptions.options.push({ name: null, value: roundedValue })
    }
  }

  return { minOptions, maxOptions }
}

export const BEDS: CustomCompOptionProps = {
  label: "Bed",
  sublabel: "SUBJECT PROPERTY",
  options: [
    { name: "-2", value: -2 },
    { name: "-1", value: -1 },
    { name: "+1", value: 1 },
    { name: "+2", value: 2 },
  ],
}

export const BATHS: CustomCompOptionProps = {
  label: "Bath",
  sublabel: "SUBJECT PROPERTY",
  options: [
    { name: "-2", value: -2 },
    { name: "-1.5", value: -1.5 },
    { name: "-1", value: -1 },
    { name: "-.5", value: -0.5 },
    { name: "+.5", value: 0.5 },
    { name: "+1", value: 1 },
    { name: "+1.5", value: 1.5 },
    { name: "+2", value: 2 },
  ],
}

export function generateBedOptions(baseBeds: number) {
  const bedOptions = BEDS.options
    .map((opt) => ({
      ...opt,
      value: typeof opt.value === "number" ? baseBeds + opt.value : null,
    }))
    .filter((opt) => opt.value !== null)

  const minOptions = {
    ...BEDS,
    options: bedOptions.filter((opt) => opt.value! <= baseBeds),
  }

  if (!minOptions.options.some((opt) => opt.value === baseBeds)) {
    minOptions.options.push({ name: null, value: baseBeds })
  }

  const maxOptions = {
    ...BEDS,
    options: bedOptions.filter((opt) => opt.value! >= baseBeds),
  }

  if (!maxOptions.options.some((opt) => opt.value === baseBeds)) {
    maxOptions.options.push({ name: null, value: baseBeds })
  }

  minOptions.options.sort((a, b) => a.value! - b.value!)
  maxOptions.options.sort((a, b) => a.value! - b.value!)

  return { minOptions, maxOptions }
}

export function generateBathOptions(baseBaths: number) {
  const bathOptions = BATHS.options
    .map((opt) => ({
      ...opt,
      value: typeof opt.value === "number" ? baseBaths + opt.value : null,
    }))
    .filter((opt) => opt.value !== null)

  const minOptions = {
    ...BATHS,
    options: bathOptions.filter((opt) => opt.value! <= baseBaths),
  }

  if (!minOptions.options.some((opt) => opt.value === baseBaths)) {
    minOptions.options.push({ name: null, value: baseBaths })
  }

  const maxOptions = {
    ...BATHS,
    options: bathOptions.filter((opt) => opt.value! >= baseBaths),
  }

  if (!maxOptions.options.some((opt) => opt.value === baseBaths)) {
    maxOptions.options.push({ name: null, value: baseBaths })
  }

  minOptions.options.sort((a, b) => a.value! - b.value!)
  maxOptions.options.sort((a, b) => a.value! - b.value!)

  return { minOptions, maxOptions }
}
